import type { Activity, ItineraryDay, JournalEntry, TripPhoto } from '../types';

export interface StopGroup {
  day: ItineraryDay;
  stops: Activity[];
}

/**
 * ADR-0056: the bulk "assign place" sheet offers every stop of the trip, by day —
 * not only the selection's day. Narrowing it to one day makes the gesture
 * disappear in silence.
 */
export function stopGroups(days: ItineraryDay[]): StopGroup[] {
  return days
    .map(day => ({ day, stops: day.activities.filter(a => !a.isOrigin) }))
    .filter(g => g.stops.length > 0);
}

/**
 * ADR-0056: the place rules the day. Sending only `activityId` gets a 400 for
 * every photo on another day (the server wants the stop on the photo's day).
 */
export function stopAssignmentPatch(stop: Pick<Activity, 'id' | 'dayId'>): { dayId: string; activityId: string } {
  return { dayId: stop.dayId, activityId: stop.id };
}

export function toggleId(ids: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(ids);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

/** Runs `fn` over every item, `size` at a time; one failure does not stop the rest. */
export async function settleInBatches<T, R>(
  items: T[],
  size: number,
  fn: (item: T) => Promise<R>,
): Promise<{ done: R[]; failed: number }> {
  const done: R[] = [];
  let failed = 0;
  for (let i = 0; i < items.length; i += size) {
    const results = await Promise.allSettled(items.slice(i, i + size).map(fn));
    for (const r of results) {
      if (r.status === 'fulfilled') done.push(r.value); else failed += 1;
    }
  }
  return { done, failed };
}

/** The server drops a photo from its diary entry when it changes day (ADR-0043). */
export function entriesAfterUpdate(entries: JournalEntry[], updated: TripPhoto[]): JournalEntry[] {
  const byId = new Map(updated.map(p => [p.id, p]));
  return entries.map(e => ({
    ...e,
    photoIds: e.photoIds.filter(id => {
      const p = byId.get(id);
      return !p || p.journalEntryId === e.id;
    }),
  }));
}

export function entriesAfterDelete(entries: JournalEntry[], deletedIds: ReadonlySet<string>): JournalEntry[] {
  return entries.map(e => ({ ...e, photoIds: e.photoIds.filter(id => !deletedIds.has(id)) }));
}
